/**
 * Single Source of Truth for Business Information
 * 
 * All business contact details, hours, and location data are sourced from coreSchemas.ts
 * Import from this file in ALL UI components — never hardcode business info.
 * 
 * Usage:
 * import { PHONE_DISPLAY, EMAIL, ADDRESS_FULL, HOURS_LABEL } from '@/lib/businessInfo';
 */

import { CORE_BUSINESS, SERVICE_AREAS } from './coreSchemas';

// ── Phone Numbers ──
export const PHONE_DISPLAY = CORE_BUSINESS.phone;
export const PHONE_TEL = CORE_BUSINESS.phoneTel;
export const PHONE_E164 = CORE_BUSINESS.phoneE164;
export const PHONE_RAW = CORE_BUSINESS.telephone; // Schema format

// ── Email ──
export const EMAIL = CORE_BUSINESS.email;

// ── Address Components ──
export const ADDRESS_STREET = CORE_BUSINESS.address.streetAddress;
export const ADDRESS_CITY = CORE_BUSINESS.address.addressLocality;
export const ADDRESS_STATE = CORE_BUSINESS.address.addressRegion;
export const ADDRESS_ZIP = CORE_BUSINESS.address.postalCode;

// ── Full Address Formats ──
export const ADDRESS_FULL = `${ADDRESS_STREET}, ${ADDRESS_CITY}, ${ADDRESS_STATE}`;
export const ADDRESS_FULL_WITH_ZIP = `${ADDRESS_STREET}, ${ADDRESS_CITY}, ${ADDRESS_STATE} ${ADDRESS_ZIP}`;

// ── Maps & Location ──
export const MAPS_URL = CORE_BUSINESS.mapsUrl;
export const GEO_LATITUDE = CORE_BUSINESS.geo.latitude;
export const GEO_LONGITUDE = CORE_BUSINESS.geo.longitude;

// ── Hours ──
export const HOURS_LABEL = CORE_BUSINESS.hoursLabel;
export const OPENING_HOURS = CORE_BUSINESS.openingHours;

// ── Business Identity ──
export const BUSINESS_NAME = CORE_BUSINESS.name;
export const BUSINESS_URL = CORE_BUSINESS.url;
export const BUSINESS_LOGO = CORE_BUSINESS.logo;
export const BUSINESS_SLOGAN = CORE_BUSINESS.slogan;

// ── Social Media ──
export const SOCIAL_PROFILES = CORE_BUSINESS.sameAs;
export const FACEBOOK_URL = CORE_BUSINESS.sameAs[1];
export const INSTAGRAM_URL = CORE_BUSINESS.sameAs[3];
export const TWITTER_URL = CORE_BUSINESS.sameAs[4];
export const TIKTOK_URL = CORE_BUSINESS.sameAs[5];
export const YOUTUBE_URL = CORE_BUSINESS.sameAs[6];

// ── Service Area ──
export const SERVICE_AREA = CORE_BUSINESS.areaServed;
export const SERVICE_AREA_CITIES = SERVICE_AREAS;
export const PRICE_RANGE = CORE_BUSINESS.priceRange;

// ── Trust Signals & Guarantees (Single Source of Truth) ──
export const TRUST_STATS = {
  docFee: {
    value: '$250',
    label: 'Flat Doc Fee',
    description: 'No surprises. Always.',
    icon: 'ri-money-dollar-circle-line',
  },
  delivery: {
    value: '50 mi',
    label: 'Free Delivery',
    description: 'We bring it to you',
    icon: 'ri-truck-line',
  },
  charity: {
    value: '$100',
    label: 'Per Sale to Dog Rescue',
    description: 'Oregon Dog Rescue',
    icon: 'ri-heart-line',
  },
  hiddenFees: {
    value: 'Zero',
    label: 'Hidden Fees',
    description: 'Guaranteed',
    icon: 'ri-shield-check-line',
  },
  salesTax: {
    value: '0%',
    label: 'Oregon Sales Tax',
    description: 'Save thousands',
    icon: 'ri-percent-line',
  },
  grandOpening: {
    value: '3/31/26',
    label: 'Grand Opening',
    description: 'Join us!',
    icon: 'ri-flag-line',
  },
} as const;

export const TRUST_FEATURES = [
  {
    icon: 'ri-shield-check-line',
    title: 'Every Vehicle Inspected',
    description: 'Thorough multi-point inspection',
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'No Hidden Fees',
    description: 'Transparent pricing always',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'All Credit Welcome',
    description: 'Financing options available',
  },
  {
    icon: 'ri-truck-line',
    title: 'Coast-Wide Delivery',
    description: 'We bring it to you',
  },
  {
    icon: 'ri-percent-line',
    title: 'Zero Markup on Rates',
    description: 'Honest financing',
  },
  {
    icon: 'ri-eye-line',
    title: 'Inspected & Priced Honestly',
    description: 'No dealer games',
  },
] as const;

// Google Review URL
export const GOOGLE_REVIEW_URL = 'https://share.google/vqOn8ooyXd5MkpaOe';

// ── Why Buy From Us - Benefits & Comparison (Single Source of Truth) ──

export const WHY_BUY_BENEFITS = [
  {
    icon: 'ri-money-dollar-circle-line',
    title: '$250 Flat Doc Fee',
    description: 'Oregon dealers average $500–$1,200 in doc fees. We charge $250. That\'s it. The savings alone could cover a tank of gas or a nice dinner.',
    link: null,
    linkLabel: null,
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Inspected Before You See It',
    description: 'Every vehicle passes a full safety check — brakes, lights, steering, suspension. If it doesn\'t pass, it doesn\'t go on the lot.',
    link: null,
    linkLabel: null,
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Financing for Real Life',
    description: 'Bad credit, no credit, rebuilding — we work with lenders who deal in reality. You deserve reliable transportation and <a href="/financing" class="text-[#7a5208] hover:text-[#1A2332] font-semibold underline decoration-1 underline-offset-2 transition-colors">car financing for all credit types</a>.',
    link: '/financing',
    linkLabel: 'Explore Financing',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Free CarFax — No Strings',
    description: 'Full accident history, service records, ownership count — handed to you before you even ask. Knowledge is yours.',
    link: null,
    linkLabel: null,
  },
  {
    icon: 'ri-truck-line',
    title: 'We Come to You',
    description: 'Free delivery within 50 miles. Browse online, get approved, and have your car show up at your door. We offer <a href="/oregon-coast-delivery" class="text-[#7a5208] hover:text-[#1A2332] font-semibold underline decoration-1 underline-offset-2 transition-colors">free delivery throughout the Oregon Coast</a>.',
    link: '/inventory',
    linkLabel: 'Browse Inventory',
  },
  {
    icon: 'ri-heart-3-line',
    title: 'Your Purchase Saves a Dog',
    description: '$100 from every sale goes directly to Oregon Dog Rescue — funding real rescues, vet care, and adoptions.',
    link: '/community-impact',
    linkLabel: 'See Our Impact',
  },
  {
    icon: 'ri-tools-line',
    title: 'Honest Condition Disclosure',
    description: 'Every vehicle is inspected and reconditioned — and we tell you exactly what we found, good or bad.',
    link: null,
    linkLabel: null,
  },
  {
    icon: 'ri-search-eye-line',
    title: 'Full Transparency Upfront',
    description: 'Complete vehicle history, honest condition disclosures, and no surprises at signing.',
    link: null,
    linkLabel: null,
  },
] as const;

export const WHY_BUY_KEY_POINTS = [
  {
    icon: 'ri-time-line',
    title: 'Fast Buying Process',
    description: 'No games, no pressure. Browse online, get approved, and drive away — often same day.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Transparent Pricing',
    description: 'Every price is final. No hidden fees, no surprises at signing. What you see is what you pay.',
  },
  {
    icon: 'ri-shield-cross-line',
    title: 'No Dealership Games',
    description: 'No bait-and-switch. No "let me talk to my manager." Just honest, straightforward car buying.',
  },
  {
    icon: 'ri-user-smile-line',
    title: 'Friendly Service',
    description: 'We treat you like a neighbor, not a transaction. Real people, real conversations.',
  },
] as const;

export const WHY_BUY_COMPARISON = [
  { label: 'Doc Fee', us: '$250 flat', them: '$500–$1,200' },
  { label: 'Price Transparency', us: 'Full — before you ask', them: 'Hidden until signing' },
  { label: 'Vehicle Inspection', us: 'Every car, every time', them: 'Varies — often skipped' },
  { label: 'CarFax Report', us: 'Free, no strings', them: 'Often charged extra' },
  { label: 'Sales Pressure', us: 'Zero. Guaranteed.', them: 'Standard practice' },
  { label: 'Delivery', us: 'Free within 50 mi', them: 'Rarely offered' },
  { label: 'Charity Donation', us: '$100 to dog rescue', them: 'None' },
] as const;

// ── Sell Your Car - Benefits (Single Source of Truth) ──

export const SELL_CAR_BENEFITS = [
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Top Dollar Offers',
    description: 'We use real-time market data to ensure you get a competitive, fair price — often more than trade-in value at traditional dealerships.',
  },
  {
    icon: 'ri-timer-line',
    title: 'Fast & Easy Process',
    description: 'No weeks of waiting for private buyers. Get an offer within 24 hours and cash in hand as fast as the same day.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Transparent Pricing',
    description: '$250 flat doc fee when you buy from us — that\'s the only dealer fee. When we buy your car, our offer is what you get with no surprise deductions.',
  },
  {
    icon: 'ri-car-line',
    title: 'We Buy All Makes & Models',
    description: 'Cars, trucks, SUVs, vans — running or not. High mileage, older models, cosmetic damage? We still want to make you an offer.',
  },
  {
    icon: 'ri-file-paper-2-line',
    title: 'We Handle the Paperwork',
    description: 'Title transfer, DMV forms, lien payoffs — we take care of everything so you don\'t have to deal with any of it.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Free Pickup Available',
    description: 'Can\'t bring your car to us? No problem. We offer free vehicle pickup throughout the Oregon Coast and Southwest Washington.',
    linkText: 'Oregon Coast and Southwest Washington',
    linkUrl: '/service-areas',
  },
] as const;